(ns hai.notes.mutations
  "Builders for the `:resolve` maps behind every note mutation. Each returns
   `{:query ... :transform-fn ...}` with the fresh note unwrapped from the
   response; surfaces assoc their own `:store-in` and follow-up fx on top.
   Mutations that feed a mounted view join the full editor shape
   (`hai.notes.eql/note-eql`); callers that discard the result pass a narrower
   join."
  (:require
   [clojure.string :as str]
   [hai.entity.staging :as staging]
   [hai.notes.eql :as eql]))

(defn empty-payload?
  "Whether a composer's staged payload — `:text`, `:urls`, `:tags`,
   `:contacts`, `:notes`, `:tasks` — carries nothing."
  [{:keys [text urls tags contacts notes tasks]}]
  (and (str/blank? text)
       (empty? urls) (empty? tags) (empty? contacts) (empty? notes) (empty? tasks)))

(defn- added-ids
  "What the composer newly staged: what came back, minus what the note already
   has, by `id-attr` (`identity` for plain values such as URLs). `note/upsert`
   asserts the requester owns every tag and contact it is sent and can read
   every note and task — re-sending one merely riding along on a note shared
   by someone else rejects the whole save."
  [id-attr linked staged]
  (let [linked-ids (set (keep id-attr linked))]
    (vec (remove linked-ids (keep id-attr staged)))))

(defn- dropped-ids
  "What the note has that the composer dropped: what it has, minus what came
   back staged."
  [id-attr linked staged]
  (let [staged-ids (set (keep id-attr staged))]
    (vec (remove staged-ids (keep id-attr linked)))))

(defn- note-urls [note]
  (keep :activity/url (:note/activities note)))

(defn- upsert-params
  "`note/upsert` params filing a staged payload onto `note` (nil creates one).
   The upsert links additively, so an existing note gets only the deltas —
   what was staged that it lacks, and `:note/untag-uuids` for the tags it
   lost; the links it lost are [[detach-forms]]. A tag picked from search
   travels by uuid, one typed fresh by name — unless the note already carries
   that name, in which case it is that tag."
  [{:keys [note source]} {:keys [text urls tags contacts notes tasks]}]
  (let [tags (staging/reconcile-tags (:note/tags note) tags)
        link-urls (added-ids identity (note-urls note) urls)
        tag-uuids (added-ids :group/uuid (:note/tags note) tags)
        tag-names (vec (keep :tag/name (remove :group/uuid tags)))
        untag-uuids (dropped-ids :group/uuid (:note/tags note) tags)
        contact-uuids (added-ids :contact/uuid (:note/contacts note) contacts)
        note-uuids (added-ids :note/uuid (:note/notes note) notes)
        task-uuids (added-ids :task/uuid (:note/tasks note) tasks)]
    (cond-> {:note/text (or text "")}
      (:note/uuid note) (assoc :note/uuid (:note/uuid note))
      source (assoc :note/source source)
      (seq link-urls) (assoc :activity/urls link-urls)
      (seq tag-uuids) (assoc :note/tag-uuids tag-uuids)
      (seq tag-names) (assoc :note/tag-names tag-names)
      (seq untag-uuids) (assoc :note/untag-uuids untag-uuids)
      (seq contact-uuids) (assoc :note/contact-uuids contact-uuids)
      (seq note-uuids) (assoc :note/note-uuids note-uuids)
      (seq task-uuids) (assoc :note/task-uuids task-uuids))))

(defn- detach-forms
  "One `note/remove-*` form per link the composer dropped off an existing
   note."
  [note {:keys [urls contacts notes tasks]}]
  (let [note-uuid (:note/uuid note)]
    (concat
     (for [url (dropped-ids identity (note-urls note) urls)]
       (list 'note/remove-link {:note/uuid note-uuid :activity/url url}))
     (for [contact-uuid (dropped-ids :contact/uuid (:note/contacts note) contacts)]
       (list 'note/remove-contact {:note/uuid note-uuid :contact/uuid contact-uuid}))
     (for [linked-uuid (dropped-ids :note/uuid (:note/notes note) notes)]
       (list 'note/remove-note {:note/uuid note-uuid :note/linked-uuid linked-uuid}))
     (for [task-uuid (dropped-ids :task/uuid (:note/tasks note) tasks)]
       (list 'note/remove-task {:note/uuid note-uuid :task/uuid task-uuid})))))

(defn save-resolve
  "Save a composer's staged payload onto `:note` (nil creates one) in one
   request: the detaches for every link it dropped, then the `note/upsert`
   carrying the rest — so the note that comes back is the note as saved.
   `:source` is what a created note is filed under. One arg joins the full
   editor shape; pass `note-join` (e.g. `[:note/uuid]`) when the caller
   discards the result."
  ([opts staged]
   (save-resolve opts staged eql/note-eql))
  ([{:keys [note] :as opts} staged note-join]
   {:query (conj (vec (detach-forms note staged))
                 {(list 'note/upsert (upsert-params opts staged)) note-join})
    :transform-fn (fn [resp] (get resp 'note/upsert))}))

(defn delete-resolve
  [note-uuid]
  {:query [(list 'note/delete {:note/uuid note-uuid})]})
